import { getLogger, Logger } from '@foundation-player/logger';
import { isSafariBrowser } from '@foundation-player/platform-check';

export const PLAYREADY_KEY_SYSTEMS = ['com.microsoft.playready', 'com.microsoft.playready.recommendation'];

export const WIDEVINE_KEY_SYSTEMS = ['com.widevine.alpha'];

export const FAIRPLAY_KEY_SYSTEMS = ['com.apple.fps.1_0', 'com.apple.fps'];

export interface DRMSupport {
  supported: boolean;
  persistentState?: boolean;
}

/**
 * There are three different ways to check DRM support:
 *
 * - using navigator.requestMediaKeySystemAccess (modern browsers)
 * - using window.MSMediaKeys (Microsoft specific)
 * - using videoElement.canPlayType with a second parameter (older Chromium based browsers)
 *
 * We extract the common logic to this abstract class and define subclasses for
 * each of the three ways.
 *
 * These subclasses have to implement a static method to check whether the way
 * of the subclass is supported in the current client.
 * They also have to implement a method to test a specific key system.
 */
abstract class DrmProbe {
  private readonly keySystems = [
    'org.w3.clearkey', // only for completeness but not used currently
    ...WIDEVINE_KEY_SYSTEMS,
    ...PLAYREADY_KEY_SYSTEMS,
    ...FAIRPLAY_KEY_SYSTEMS,
    'com.adobe.primetime', // only for completeness but not used currently
  ];

  private readonly basicVideoCapabilities = [
    { contentType: 'video/mp4; codecs="avc1.42E01E"' },
    { contentType: 'video/webm; codecs="vp8"' },
  ];

  private readonly basicConfig: MediaKeySystemConfiguration = {
    initDataTypes: ['cenc'],
    videoCapabilities: this.basicVideoCapabilities,
  };

  private readonly offlineConfig: MediaKeySystemConfiguration = {
    videoCapabilities: this.basicVideoCapabilities,
    persistentState: 'required',
    sessionTypes: ['persistent-license'],
  };

  protected readonly configs = [this.offlineConfig, this.basicConfig];

  static canDetectDRMSupport(_videoElement: HTMLVideoElement): boolean {
    return false;
  }

  constructor(
    protected readonly videoElement: HTMLVideoElement,
    protected readonly logger: Logger,
  ) {}

  /**
   * Detect which DRM key systems are supported by the client.
   *
   * @see https://github.com/shaka-project/shaka-player/blob/a3d5dc54ff0e6eacd2af2602037cccabbd525c03/lib/media/drm_engine.js#L1812
   */
  async detectDRMSupport(): Promise<Record<string, DRMSupport>> {
    this.logger.debug('Test drm support with the following media key system configurations', this.configs);

    const supportedDrmSystems: Array<[string, DRMSupport]> = await Promise.all(
      this.keySystems.map(async (keySystem) => {
        // It seems the Safari modern EME API will allow to request a
        // MediaKeySystemAccess for the ClearKey CDM, create and update a key
        // session but playback will never start
        // Safari bug: https://bugs.webkit.org/show_bug.cgi?id=231006
        if (keySystem === 'org.w3.clearkey' && isSafariBrowser()) {
          this.logger.debug(`Disable "${keySystem}" support for Safari`);
          const support: DRMSupport = { supported: false };
          return [keySystem, support];
        }

        this.logger.debug(`Test access support for "${keySystem}"`);

        const support = await this.checkKeySystem(keySystem);

        return [keySystem, support];
      }),
    );

    this.logger.debug(
      'Detected drm support for',
      supportedDrmSystems.filter(([_keySystem, support]) => support.supported),
    );

    return Object.fromEntries(supportedDrmSystems);
  }

  protected abstract checkKeySystem(keySystem: string): Promise<DRMSupport> | DRMSupport;
}

/**
 * Subclass to test supported DRM key systems with
 * `navigator.requestMediaKeySystemAccess`.
 */
class DRMProbeWithRequestMediaKeySystemAccess extends DrmProbe {
  static canDetectDRMSupport(_videoElement: HTMLVideoElement): boolean {
    return typeof navigator.requestMediaKeySystemAccess === 'function';
  }

  protected async checkKeySystem(keySystem: string): Promise<DRMSupport> {
    let support: DRMSupport;

    try {
      this.logger.debug(`Test access support for "${keySystem}"`);
      const access = await navigator.requestMediaKeySystemAccess(keySystem, this.configs);

      // Edge doesn't return supported session types, but current versions
      // do not support persistent-license.  If sessionTypes is missing,
      // assume no support for persistent-license.
      // TODO: Polyfill Edge to return known supported session types.
      // Edge bug: https://bit.ly/2IeKzho
      const sessionTypes = access.getConfiguration().sessionTypes;
      const persistentState = sessionTypes ? sessionTypes.includes('persistent-license') : false;

      // Tizen 3.0 doesn't support persistent licenses, but reports that it
      // does.  It doesn't fail until you call update() with a license
      // response, which is way too late.
      // This is a work-around for #894.
      // TODO: Add check for Tizen 3 - 2017
      // if (isTizen3()) {
      //   persistentState = false;
      // }

      this.logger.debug(`Set preliminary support for "${keySystem}"`);
      support = {
        supported: true,
        persistentState,
      };

      this.logger.debug(`Create media keys for "${keySystem}"`);
      await access.createMediaKeys();
    } catch (e) {
      // Either the request failed or createMediaKeys failed.
      // Either way, mark drm system as unsupported
      support = {
        supported: false,
      };
      this.logger.debug(`Disable support for "${keySystem}"`, e);
    }

    return support;
  }
}

type WindowWithMSMediaKeys = Window & {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MSMediaKeys?: {
    isTypeSupported(keySystem: string): boolean;
  };
};

/**
 * Subclass to test supported DRM key systems with `MSMediaKeys.isTypeSupported`
 */
class DRMProbeWithMSMediaKeys extends DrmProbe {
  static canDetectDRMSupport(_videoElement: HTMLVideoElement): boolean {
    // MSMediaKeys is a Microsoft specific property on the window object.
    return typeof (window as unknown as WindowWithMSMediaKeys).MSMediaKeys !== 'undefined';
  }

  protected checkKeySystem(keySystem: string): DRMSupport {
    return {
      supported: (window as unknown as WindowWithMSMediaKeys).MSMediaKeys?.isTypeSupported(keySystem) || false,
    };
  }
}

/**
 * Subclass to test supported DRM key systems with
 * `videoElement.canPlayType(type, keySystem)`
 */
class DRMProbeWithCanPlayType extends DrmProbe {
  static canDetectDRMSupport(videoElement: HTMLVideoElement): boolean {
    // Weak inference: if `webkitGenerateKeyRequest` exists, we assume that
    // `canPlayType` accepts the key system as second parameter
    return 'webkitGenerateKeyRequest' in videoElement && typeof videoElement.webkitGenerateKeyRequest === 'function';
  }

  protected checkKeySystem(keySystem: string): DRMSupport {
    return {
      // Older versions of `canPlayType` accepted the key system as second
      // parameter. This second parameter is deprecated, therefore we get a
      // TypeScript error.
      //
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      supported: Boolean(this.videoElement.canPlayType('video/mp4', keySystem)),
    };
  }
}

/**
 * Check if a video element can play different drm types
 */
export function drmProbe(videoElement: HTMLVideoElement): Promise<Record<string, DRMSupport>> {
  const logger = getLogger('player.core.capability.drmProbe');
  const drmProbeSystems = [DRMProbeWithRequestMediaKeySystemAccess, DRMProbeWithMSMediaKeys, DRMProbeWithCanPlayType];

  // DrmProbeSystem references a class, therefore I prefer the name starting
  // with a capital letter
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const DrmProbeSystem = drmProbeSystems.find((system) => system.canDetectDRMSupport(videoElement));

  if (!DrmProbeSystem) {
    logger.warn('No support for checking DRM found');
    return Promise.resolve({});
  }

  const drmProbeSystem = new DrmProbeSystem(videoElement, logger);

  return drmProbeSystem.detectDRMSupport();
}
